import { CategoryView, DocumentList, PageList } from '@components';

import { LoadingStatus, ProductCategory, SearchContentSection } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { useAppDispatch, useAppSelector } from '@hooks';
import { Box } from '@mui/material';
import { styled } from '@mui/system';
import {
  getFullSearchProductTotalCount,
  getProductsCategoryLink,
  getSearchResults,
  getSearchSection,
  setProductsCategoryLink,
  setSearchOffset,
  setSearchSection,
} from '@store/search/search.slice';
import { t } from '@translations';
// STYLES
//--------
const ResultsSectionsWrapper = styled(Box)``;

// TYPES
//---------
export type SearchResultsSectionListProps = {
  statuses: { [Key in SearchContentSection]: LoadingStatus }; // Sections status
  itemCount?: number;
};

export type SearchResultsSectionProps = {
  status: LoadingStatus;
};

// COMPONENT
//-----------

/**
 * Result Sections
 */
export const SearchResultsSections = ({ statuses }: SearchResultsSectionListProps) => {
  const currentSection = useAppSelector(getSearchSection);
  const { products, articles, documents, accessories } = statuses;
  return (
    <ResultsSectionsWrapper>
      {(currentSection === 'products' || currentSection === 'accessories') && (
        <ProductsResultSection status={currentSection === 'products' ? products : accessories} />
      )}
      {currentSection === 'articles' && <ContentResultSection status={articles} />}
      {currentSection === 'documents' && <DocumentsResultSection status={documents} />}
    </ResultsSectionsWrapper>
  );
};

export const ContentResultSection = ({ status }: SearchResultsSectionProps) => {
  const results = useAppSelector(getSearchResults);
  const blocks = results.articles?.items;
  const isSectionBusy = status.isFetching || status.isLoading;
  return (
    <>
      <PageList blocks={blocks} useCard={true} isWorking={isSectionBusy} />
    </>
  );
};

export const ProductsResultSection = ({ status }: SearchResultsSectionProps) => {
  const dispatch = useAppDispatch();
  const results = useAppSelector(getSearchResults);
  const totalCount = useAppSelector(getFullSearchProductTotalCount);
  const isAccessories = useAppSelector(getProductsCategoryLink) === 'Accessories';
  const productItems = isAccessories ? results.accessories?.items : results.products?.items;

  const categories = [
    ...(results.products?.categories || []),
    {
      categoryLink: 'Accessories',
      displayName: t(TextCodes.Search.Accessories),
      productCount: results.accessories?.totalCount,
    },
  ];

  const handleCategoryChange = (category: ProductCategory) => {
    if (category.categoryLink === 'Accessories') {
      dispatch(setSearchSection('accessories'));
      dispatch(setProductsCategoryLink('Accessories'));
      dispatch(setSearchOffset(0));
      return;
    }
    dispatch(setSearchSection('products'));
    dispatch(setSearchOffset(0));
    dispatch(setProductsCategoryLink(category.categoryLink));
  };

  // Active item
  const currentCategoryLink = useAppSelector(getProductsCategoryLink);
  const isCategorySelected = (category: ProductCategory) =>
    currentCategoryLink === category.categoryLink;

  const isWorking = status.isFetching || status.isLoading;

  return (
    <CategoryView
      skipEmptyCategories={true}
      productItems={productItems}
      totalCount={totalCount}
      categories={categories}
      isWorking={isWorking}
      isCategorySelected={isCategorySelected}
      handleCategoryChange={handleCategoryChange}
      isCategoryOpened={isCategorySelected}
      // TODO [KF] number should not changes with filters
    />
  );
};

export const DocumentsResultSection = ({ status }: SearchResultsSectionProps) => {
  const results = useAppSelector(getSearchResults);
  const items = results.documents?.items;
  const isSectionBusy = status.isFetching || status.isLoading;
  return (
    <>
      <DocumentList documents={items} isWorking={isSectionBusy} showAlert />
    </>
  );
};
